import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Spinner, Table, Button } from 'react-bootstrap';
import Select from 'react-select';
import { PartnersAPI, SurveysAPI } from '../../API';
import { Form as PartnerForm } from './dashboardpartners';
import { buildPartnerSurveyLink } from '../../Utils/portalUrls';
import { api } from '../../axiosConfig';
import { surveysURL, baseApiURL } from '../../Utils/urls';

type PartnerDetail = {
  partnerId: string;
  partner_name: string;
  quota: any;
  surveyType: string;
  selectedMarkets: string[] | null;
  completed: any;
  terminated: any;
  overquota: any;
  qualityTerminated: any;
  // PQ-split: prescreener-fail QT (isPreScreenTerminated=true). Returned by
  // getpantnerdetailsmain (already split server-side at admin surveys.controllers.js
  // ~line 4658) and rendered as the "PQ Terminated" column next to qualityTerminated.
  pq_terminated_count?: number;
  verisoul_terminate_count?: number;
  hit: any;
  dropout: any;
  ir: any;
  median_loi: any;
  // Stage-2 display aggregation. 'parent' = partner cascades to all children
  // (default — no extra marker rendered). An array of child shortNames means
  // the partner is attached at child level only, or restricted to specific
  // markets — rendered as an "Applies to: …" badge.
  appliesTo?: 'parent' | string[];
  // Ask 6 — per-partner pool counts from survey_unique_links (claimedFor =
  // 'partner_quota'). poolClaimedCount drives the Download CSV disable gate;
  // both fields default to 0 server-side when no rows exist for the partner.
  poolClaimedCount?: number;
  poolConsumedCount?: number;
};

type PartnerOption = { label: string; value: string };

type ChildSurvey = {
  id: string;
  uniqueid: string;
  shortName: string | null;
  childProjectName?: string;
  country: string;
  name: string;
};

/** Per-child prescreener config used to populate the partner modal's
 *  "Visible questions" checkbox list. Empty `questions` array = market has
 *  prescreening disabled or no picks yet. */
export type ChildPrescreenConfig = {
  // Child survey id — the market identity (partner→market is id-based). shortName
  // is display-only and collides for same-country duplicates.
  id: string;
  shortName: string;
  // Free-form per-child label to disambiguate same-country duplicates in the UI.
  childProjectName?: string;
  country: string;
  preQuestionsYN: 'Y' | 'N';
  questions: Array<{ question_id: string; question: string; questionType: string; type: string }>;
};

type Props = {
  isEditMode: boolean;
  parentSurveyId?: string;
  projectName?: string;
  childSurveyIds?: string[];
  surveyType?: string;
  userLimitCommitted?: any;
  childCountries?: { label: string; value: string; shortName: string; id?: string; childProjectName?: string }[];
  isParentSurvey?: boolean;
  // The currently-loaded survey row from Form's fetchDetails. Used to detect
  // child-direct context (survey.parentid set) without re-fetching via getOne,
  // which proved unreliable in earlier instrumentation (branch d-graceful-empty
  // was firing on IN-child-direct because the in-loadData getOne failed).
  survey?: any;
};

// Strip the `${parentName}_${shortName}_` prefix from a stored childProjectName
// to recover just the PM-typed suffix portion for display. Mirrors the suffix
// derivation in ChildSurveyTab/ParentProjectTab's input-group (including the
// "orphan" branch for legacy rows created when parent name was empty). Returns
// '' when the stored value is the bare base prefix, missing, or in an unknown
// shape — callers gate parens/separators on the empty-string result.
function extractDisplaySuffix(fullName: string | null | undefined, parentName: string, shortName: string): string {
  if (!fullName) return '';
  const trimmed = String(fullName).trim();
  if (!trimmed) return '';
  const base = `${parentName}_${shortName}`;
  const fullPrefix = `${base}_`;
  if (trimmed === base) return '';
  if (trimmed.startsWith(fullPrefix)) return trimmed.substring(fullPrefix.length);
  const orphanBase = `_${shortName}`;
  const orphanPrefix = `_${shortName}_`;
  if (trimmed === orphanBase) return '';
  if (trimmed.startsWith(orphanPrefix)) return trimmed.substring(orphanPrefix.length);

  // Fallback: If parent name changed, the string might be `OldParent_IN_Suffix`.
  const fallbackPrefix = `_${shortName}_`;
  const lastIndex = trimmed.lastIndexOf(fallbackPrefix);
  if (lastIndex !== -1) {
    return trimmed.substring(lastIndex + fallbackPrefix.length);
  }
  if (trimmed.endsWith(`_${shortName}`)) return '';

  return '';
}

enum LoadStatus { None, Loading, Loaded, Error }

export function PartnersTab({
  isEditMode,
  parentSurveyId,
  projectName,
  surveyType,
  userLimitCommitted,
  childCountries = [],
  isParentSurvey = true,
  survey,
}: Props) {
  const [partnerDetails, setPartnerDetails] = useState<PartnerDetail[]>([]);
  const [allPartners, setAllPartners] = useState<PartnerOption[]>([]);
  const [loadStatus, setLoadStatus] = useState(LoadStatus.None);
  const [error, setError] = useState<string | null>(null);
  const [selectedNewPartner, setSelectedNewPartner] = useState<PartnerOption | null>(null);
  const [adding, setAdding] = useState(false);

  // Child surveys for link generation
  const [childSurveys, setChildSurveys] = useState<ChildSurvey[]>([]);
  // Per-child prescreener configs — drives the partner modal's per-question
  // visibility checkbox list. Loaded once when the partners tab loads.
  const [childPrescreenConfigs, setChildPrescreenConfigs] = useState<ChildPrescreenConfig[]>([]);
  const [expandedLinks, setExpandedLinks] = useState<Set<string>>(new Set());
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalPartnerId, setModalPartnerId] = useState('');
  const [modalPartnerName, setModalPartnerName] = useState('');
  const [modalType, setModalType] = useState<'create' | 'update'>('create');


  const loadData = useCallback(() => {
    if (!parentSurveyId) return;
    setLoadStatus(LoadStatus.Loading);
    setError(null);
    Promise.all([
      SurveysAPI.getpantnerdetailsmain(parentSurveyId),
      PartnersAPI.getPartners(10000),
      SurveysAPI.getChildSurveys(parentSurveyId),
    ])
      .then(async ([details, partners, children]) => {
        setPartnerDetails(Array.isArray(details) ? details : []);
        const opts = (partners || []).map((p: any) => ({ label: p.name, value: p.id }));
        setAllPartners(opts);

        // Resolve the actual list of child markets to drive the partner modal.
        //
        // The `parentSurveyId` prop holds whatever survey is currently open in
        // Form.tsx — parent OR child. When opened from a child-direct context,
        // `getChildSurveys(<childId>)` returns [] because children have no
        // children of their own. Recover via the `survey` prop (passed in
        // from Form.state.data, already loaded by Form's fetchDetails — no
        // round trip needed):
        //   a) children non-empty → use them (parent-direct context).
        //   b) survey.parentid set → re-fetch siblings from the real parent
        //      id (child-direct context).
        //   c) survey is standalone with shortName/country → synthesize a
        //      single-entry list (legacy edge case, no children, no parent).
        //   d) otherwise → keep the empty list, modal degrades gracefully.
        let resolvedChildren: any[] = [];
        if (Array.isArray(children) && children.length > 0) {
          resolvedChildren = children;
        } else if (survey && survey.parentid) {
          const siblings = await SurveysAPI.getChildSurveys(survey.parentid);
          resolvedChildren = Array.isArray(siblings) ? siblings : [];
        } else if (survey && (survey.shortName || survey.country)) {
          resolvedChildren = [survey];
        }

        const filteredChildren = resolvedChildren.filter((c: any) => c.shortName || c.country);
        setChildSurveys(
          filteredChildren.map((c: any) => ({
            id: c.id,
            uniqueid: c.uniqueid,
            shortName: c.shortName || null,
            childProjectName: c.childProjectName ?? '',
            country: c.country,
            name: c.name,
          }))
        );

        // Per-child prescreener configs. Best-effort N+1 — markets are typically
        // a handful per project. Failures here are logged and downgrade the
        // partner modal to "no per-question checkboxes available."
        //
        // Bug 1 fix: on a CHILD-direct context (!isParentSurvey), the partner
        // config modal must only offer THIS child's country's prescreening
        // questions — not every family market's. resolvedChildren above is the
        // full sibling set (re-fetched from the parent for child-direct context),
        // so scope it to the opened child here, mirroring the Links list's
        // existing per-child filter (surveysToShow: c.id === survey.id). Parent
        // context is unchanged (isParentSurvey true → all markets, as before).
        const configChildren = (!isParentSurvey && survey?.id)
          ? filteredChildren.filter((c: any) => c.id === survey.id)
          : filteredChildren;
        const configs: ChildPrescreenConfig[] = await Promise.all(
          configChildren
            .filter((c: any) => c.shortName)
            .map(async (c: any) => {
              const empty: ChildPrescreenConfig = {
                id: c.id,
                shortName: c.shortName,
                childProjectName: c.childProjectName ?? '',
                country: c.country,
                preQuestionsYN: c.preQuestionsYN === 'Y' ? 'Y' : 'N',
                questions: [],
              };
              if (c.preQuestionsYN !== 'Y') return empty;
              try {
                const childData = await SurveysAPI.getOne(c.id);
                const surveyquestion = (childData && childData.surveyquestion) || [];
                empty.questions = surveyquestion.map((sq: any) => ({
                  question_id: sq.question_id,
                  question: sq.question || '',
                  questionType: sq.questionType || '',
                  type: sq.type || '',
                }));
                return empty;
              } catch (_e) {
                return empty;
              }
            })
        );
        setChildPrescreenConfigs(configs);
        setLoadStatus(LoadStatus.Loaded);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load partners');
        setLoadStatus(LoadStatus.Error);
      });
  }, [parentSurveyId, survey]);

  useEffect(() => {
    if (isEditMode && parentSurveyId) {
      loadData();
    }
  }, [isEditMode, parentSurveyId, loadData]);

  if (!isEditMode) {
    return (
      <div className="p-4 text-center">
        <h5>Partners</h5>
        <p className="text-muted">
          Partners can be added after the survey is created.<br />
          Save the survey first, then manage partners from the Partners tab.
        </p>
        <p className="text-muted small">
          Partners added to the parent project will automatically cascade to all child country surveys.
        </p>
      </div>
    );
  }

  const assignedPartnerIds = new Set(partnerDetails.map((d) => d.partnerId));
  const availableToAdd = allPartners.filter((p) => !assignedPartnerIds.has(p.value));

  const openModal = (partnerId: string, partnerName: string, type: 'create' | 'update') => {
    setModalPartnerId(partnerId);
    setModalPartnerName(partnerName);
    setModalType(type);
    setShowModal(true);
  };

  // Phase 6 — Proxy URL (partner Mode B). One meta-URL per partner; built
  // client-side against the admin backend host (baseApiURL = REACT_APP_BASE_URL_API,
  // the same host the app's API calls target, where /p/proxy is served). Local
  // dev fallback: http://localhost:9001. {respondent_id} is a literal placeholder.
  const proxyUrlFor = (partnerId: string) =>
    `${baseApiURL || 'http://localhost:9001'}/p/proxy/${parentSurveyId}/${partnerId}?rid={respondent_id}`;

  const isUniqueLinksSurvey = survey?.useUniqueLinks === true;

  const handleAddClick = async () => {
    if (!selectedNewPartner || !parentSurveyId) return;
    setAdding(true);
    try {
      await SurveysAPI.addPartners({
        partners: [{ surveyId: parentSurveyId, partnerId: selectedNewPartner.value, includesid: false }],
      });
      openModal(selectedNewPartner.value, selectedNewPartner.label, 'create');
      setSelectedNewPartner(null);
    } catch (err: any) {
      setError(err.message || 'Failed to add partner');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (partnerId: string, partnerName: string) => {
    if (!parentSurveyId) return;
    if (!window.confirm(`Remove partner "${partnerName}" from this survey?`)) return;
    try {
      await PartnersAPI.deletepartnerurl(partnerId, parentSurveyId);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete partner');
    }
  };

  const handleModalSubmit = () => {
    setShowModal(false);
    loadData();
  };

  const toggleLinks = (partnerId: string) => {
    setExpandedLinks((prev) => {
      const next = new Set(prev);
      if (next.has(partnerId)) {
        next.delete(partnerId);
      } else {
        next.add(partnerId);
      }
      return next;
    });
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedLink(key);
      setTimeout(() => setCopiedLink(null), 2000);
    });
  };

  /**
   * Copy ALL per-market partner survey links for a given partner to the
   * clipboard in one click. Every link points at the brand-neutral surveys
   * host (SURVEYS_BASE_URL — `https://surveys.dataxing.com` in production,
   * configurable via REACT_APP_SURVEYS_BASE_URL). The path is `/p`, a
   * backend-only 302 redirect — no React rendering. Works for every market,
   * panel + non-panel. Format (one line per market):
   *   IN - English | https://surveys.dataxing.com/p?partnerid=…&surveyid=…&rid={respondent_id}
   *   US | https://surveys.dataxing.com/p?partnerid=…&surveyid=…&rid={respondent_id}
   * Suffix is the PM-typed portion of childProjectName (e.g. "English") —
   * omitted, along with the " - " separator, when not present. The market
   * label still varies per child survey, but the host is shared. The literal
   * `{respondent_id}` placeholder is preserved so partners can substitute
   * their own respondent identifier.
   */
  const copyAllLinks = (detail: PartnerDetail) => {
    const links = getLinksForPartner(detail);
    if (links.length === 0) return;
    const joined = links
      .map(({ url, country, name }) => {
        const suffix = extractDisplaySuffix(name, projectName || '', country);
        const label = suffix ? `${country} - ${suffix}` : country;
        return `${label} | ${url}`;
      })
      .join('\n');
    const key = `${detail.partnerId}-ALL`;
    copyToClipboard(joined, key);
  };

  /** Returns the child surveys visible to a partner based on their selectedMarkets setting.
   *  Each row carries `country` + `name` (the child project label) + `id` so the
   *  Links panel can disambiguate same-country duplicates (two "JP" children) and
   *  key rows uniquely. `label` is the short market badge text (unchanged). */
  const getLinksForPartner = (detail: PartnerDetail): { label: string; url: string; country: string; name: string; id: string }[] => {
    const markets: string[] | null = (() => {
      if (Array.isArray(detail.selectedMarkets)) return detail.selectedMarkets;
      if (typeof detail.selectedMarkets === 'string' && (detail.selectedMarkets as string).startsWith('[')) {
        try { return JSON.parse(detail.selectedMarkets as unknown as string); } catch { return null; }
      }
      return null;
    })();

    // markets (from selectedMarkets) now holds child IDs (id-based contract);
    // match on c.id, not c.shortName, so market-restricted partners resolve to
    // the right child surveys (and same-country duplicates resolve distinctly).
    const marketFiltered = markets && markets.length > 0
      ? childSurveys.filter((c) => markets.includes(c.id))
      : childSurveys;

    // On a child survey, scope Links to this child's own market only —
    // sibling markets are irrelevant in this context. Match on id so
    // shortName-vs-country casing/fallback never causes a miss.
    const surveysToShow = !isParentSurvey && survey?.id
      ? marketFiltered.filter((c) => c.id === survey.id)
      : marketFiltered;

    return surveysToShow.map((c) => ({
      label: c.shortName || c.country,
      // country + name carry the per-child identity for same-country-duplicate
      // disambiguation in the Links panel (display-only; link build is unchanged).
      // name prefers the free-form childProjectName, falls back to shortName.
      country: c.shortName || c.country || '',
      name: (c.childProjectName && c.childProjectName.trim()) || c.shortName || '',
      id: c.id,
      // Canonical partner-link format (see Utils/portalUrls.ts). The host is
      // always SURVEYS_BASE_URL (brand-neutral) and the path is /p — a
      // backend-only 302 redirect. marketCode is preserved on the call for
      // back-compat but the builder ignores it.
      url: buildPartnerSurveyLink({
        marketCode: c.shortName,
        partnerId: detail.partnerId,
        surveyId: c.id,
      }),
    }));
  };

  const dropoutPct = (dropout: any, hit: any) => {
    const h = Number(hit);
    if (!h) return '-';
    return ((Number(dropout) / h) * 100).toFixed(1) + '%';
  };

  const irPct = (ir: any) => {
    if (ir === undefined || ir === null || isNaN(Number(ir))) return '-';
    return Number(ir).toFixed(1) + '%';
  };

  return (
    <div className="p-3">
      {/* Context banner */}
      <div className={`alert alert-${isParentSurvey ? 'info' : 'secondary'} py-2 mb-3`} role="alert">
        {isParentSurvey
          ? 'Partners added here will be applied to all child markets automatically.'
          : 'Partners added here apply to this market only and will not affect the parent or sibling surveys.'}
      </div>

      {/* Add partner row */}
      <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
        <div style={{ minWidth: 280 }}>
          <Select
            placeholder="Select partner to add..."
            options={availableToAdd}
            value={selectedNewPartner}
            onChange={(opt: any) => setSelectedNewPartner(opt)}
            isDisabled={loadStatus === LoadStatus.Loading}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 99999 }),
              menu: (base) => ({ ...base, zIndex: 99999 }),
            }}
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={!selectedNewPartner || adding}
          onClick={handleAddClick}
        >
          {adding ? <Spinner as="span" animation="border" size="sm" className="mr-1" /> : null}
          Configure &amp; Add
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={loadData}
          title="Refresh"
          disabled={loadStatus === LoadStatus.Loading}
        >
          &#8635; Refresh
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loadStatus === LoadStatus.Loading && (
        <div className="d-flex justify-content-center p-4">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {(loadStatus === LoadStatus.Loaded || partnerDetails.length > 0) && (
        <Table responsive size="sm" bordered>
          <thead className="thead-light">
            <tr>
              <th>#</th>
              <th>Partner Name</th>
              {isParentSurvey && <th>Markets</th>}
              <th>Status</th>
              <th>Quota</th>
              <th>Completed</th>
              <th>Terminated</th>
              <th>Over Quota</th>
              <th>Quality Terminated</th>
              <th>PQ Terminated</th>
              <th>Verisoul Blocked</th>
              <th>Hits</th>
              <th>Dropout %</th>
              <th>IR %</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partnerDetails.length === 0 ? (
              <tr>
                <td colSpan={isParentSurvey ? 15 : 14} className="text-center text-muted py-3">
                  No partners assigned yet. Select a partner above to get started.
                </td>
              </tr>
            ) : (
              partnerDetails.map((detail, idx) => {
                const linksExpanded = expandedLinks.has(detail.partnerId);
                // Unique-link surveys show the partner's single proxy URL in the
                // panel instead of per-market links (the pool is shared).
                const links = linksExpanded && !isUniqueLinksSurvey ? getLinksForPartner(detail) : [];

                // Country codes appearing in more than one rendered link row →
                // their rows are indistinguishable by the badge alone (e.g. two
                // "JP" children), so those rows get the child project name
                // appended. Single-occurrence countries render unchanged.
                const linkCountryCounts: Record<string, number> = {};
                for (const l of links) {
                    if (!l.country) continue;
                    linkCountryCounts[l.country] = (linkCountryCounts[l.country] || 0) + 1;
                }
                const dupCountries = new Set(
                    Object.entries(linkCountryCounts).filter(([, n]) => n > 1).map(([c]) => c),
                );

                return (
                  <React.Fragment key={detail.partnerId}>
                    <tr>
                      <td>{idx + 1}</td>
                      <td>{detail.partner_name ?? '-'}</td>
                      {isParentSurvey && (
                        <td>
                          {(() => {
                            // Stage-2: prefer the backend-computed `appliesTo`
                            // marker — it accounts for partners attached at
                            // child level only (which `selectedMarkets`, a
                            // parent-row field, cannot express). 'parent' =
                            // cascades to all children → "All Markets".
                            if (Array.isArray(detail.appliesToData) && detail.appliesToData.length > 0 && detail.appliesToData !== 'parent') {
                              return detail.appliesToData.map((mData: any) => {
                                const suffix = extractDisplaySuffix(mData.childProjectName, survey.projectname || '', mData.shortName);
                                const display = suffix ? (
                                  <React.Fragment>
                                    <strong>{mData.shortName}</strong><span style={{ fontWeight: 'normal', color: '#e0e0e0' }}> - {suffix}</span>
                                  </React.Fragment>
                                ) : mData.shortName;
                                return (
                                  <span key={mData.shortName} className="badge badge-info mr-1">{display}</span>
                                );
                              });
                            }
                            if (Array.isArray(detail.appliesTo)) {
                              return detail.appliesTo.length > 0
                                ? detail.appliesTo.map((m) => (
                                    <span key={m} className="badge badge-info mr-1">{m}</span>
                                  ))
                                : <span className="badge badge-secondary">All Markets</span>;
                            }
                            if (detail.appliesTo === 'parent' || detail.appliesToData === 'parent') {
                              return <span className="badge badge-secondary">All Markets</span>;
                            }
                            // Fallback for any caller that predates `appliesTo`.
                            // detail.selectedMarkets now holds child IDs (id-based contract),
                            // so resolve each id → shortName via childSurveys before rendering
                            // (never print a raw UUID). appliesTo is the canonical display
                            // source above; this branch is effectively dead since
                            // getpantnerdetailsmain always emits appliesTo.
                            const markets: string[] | null = Array.isArray(detail.selectedMarkets)
                              ? detail.selectedMarkets
                              : typeof detail.selectedMarkets === 'string' && (detail.selectedMarkets as string).startsWith('[')
                                ? JSON.parse(detail.selectedMarkets as unknown as string)
                                : null;
                            const idToLabel = (id: string) => {
                              const cs = childSurveys.find((c) => c.id === id);
                              return cs ? (cs.shortName || cs.country || id) : id;
                            };
                            return markets && markets.length > 0
                              ? markets.map((m) => <span key={m} className="badge badge-info mr-1">{idToLabel(m)}</span>)
                              : <span className="badge badge-secondary">All Markets</span>;
                          })()}
                        </td>
                      )}
                      <td>{detail.surveyType ?? '-'}</td>
                      <td>{detail.quota ?? '-'}</td>
                      <td>{detail.completed ?? '0'}</td>
                      <td>{detail.terminated ?? '0'}</td>
                      <td>{detail.overquota ?? '0'}</td>
                      <td>{detail.qualityTerminated ?? '0'}</td>
                      <td>{detail.pq_terminated_count ?? 0}</td>
                      <td>{detail.verisoul_terminate_count ?? 0}</td>
                      <td>{detail.hit ?? '0'}</td>
                      <td>{dropoutPct(detail.dropout, detail.hit)}</td>
                      <td>{irPct(detail.ir)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mr-1"
                          onClick={() => openModal(detail.partnerId, detail.partner_name, 'update')}
                        >
                          Edit
                        </Button>
                        <Button
                          variant={linksExpanded ? 'info' : 'outline-info'}
                          size="sm"
                          className="mr-1"
                          // Unique-link surveys have no per-market partner links
                          // (the pool is shared), so the panel shows the partner's
                          // proxy URL instead. Shown for every partner: the
                          // /p/proxy route only needs the partner attached, and
                          // partnersSurveys.useProxyUrl is not reliably set.
                          onClick={() => toggleLinks(detail.partnerId)}
                          title={isUniqueLinksSurvey
                            ? 'Show the proxy URL (unique-links survey)'
                            : 'Show survey links per market'}
                          disabled={!isUniqueLinksSurvey && childSurveys.length === 0}
                        >
                          Links
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(detail.partnerId, detail.partner_name)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>

                    {linksExpanded && (
                      <tr>
                        <td colSpan={isParentSurvey ? 14 : 13} className="p-0">
                          <div className="p-3 bg-light border-top">
                            {isUniqueLinksSurvey ? (() => {
                              // One proxy URL per partner (not per market), so no
                              // market badge and no Copy All. Plain text, not a link:
                              // opening it with the literal {respondent_id} would
                              // hit /p/proxy and could claim a pool link for it.
                              const proxyUrl = proxyUrlFor(detail.partnerId);
                              const copyKey = `${detail.partnerId}-PROXY`;
                              return (
                                <>
                                  <div className="mb-2">
                                    <strong>Proxy URL — {detail.partner_name}</strong>
                                  </div>
                                  <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 6 }}>
                                    <span style={{ fontSize: '12px', wordBreak: 'break-all', flex: 1, fontFamily: 'monospace' }}>
                                      {proxyUrl}
                                    </span>
                                    <Button
                                      variant={copiedLink === copyKey ? 'success' : 'outline-secondary'}
                                      size="sm"
                                      style={{ flexShrink: 0, fontSize: '11px', padding: '1px 8px' }}
                                      onClick={() => copyToClipboard(proxyUrl, copyKey)}
                                    >
                                      {copiedLink === copyKey ? 'Copied!' : 'Copy'}
                                    </Button>
                                  </div>
                                </>
                              );
                            })() : (<>
                            <div className="d-flex align-items-center justify-content-between flex-wrap mb-2" style={{ gap: 8 }}>
                              <strong>Survey Links — {detail.partner_name}</strong>
                              {/* Copy ALL market links in one click — written as
                                  `{shortName}: {url}` per line. PMs can still copy
                                  individual links via the per-row Copy button below.
                                  Hidden on single-link views (child surveys, or a
                                  partner with only one market) — nothing to "all"-copy. */}
                              {links.length >= 2 && (
                                <Button
                                  variant={copiedLink === `${detail.partnerId}-ALL` ? 'success' : 'primary'}
                                  size="sm"
                                  disabled={links.length === 0}
                                  onClick={() => copyAllLinks(detail)}
                                  title="Copy every market link for this partner to the clipboard, one per line"
                                >
                                  {copiedLink === `${detail.partnerId}-ALL` ? 'Copied!' : 'Copy All Links'}
                                </Button>
                              )}
                            </div>
                            {links.length === 0 ? (
                              <span className="text-muted small">No child surveys found for this project.</span>
                            ) : (
                              links.map(({ label, url, country, name, id }) => {
                                // Key + copyKey by the unique child id (label/country
                                // collide for same-country duplicates — two "JP" rows).
                                const copyKey = `${detail.partnerId}-${id}`;
                                // Show only the PM-typed suffix (e.g. "English") next to
                                // the country badge, not the full `${parent}_${cc}_…`.
                                // Renders nothing when there is no suffix — gate the dot
                                // separator on the suffix being non-empty so we never
                                // emit a dangling "· " for bare-base rows.
                                const suffix = extractDisplaySuffix(name, projectName || '', country);
                                const showDifferentiator = dupCountries.has(country) && !!suffix;
                                return (
                                  <div key={id} className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 6 }}>
                                    <span
                                      className="badge badge-primary mr-1"
                                      style={{ minWidth: 36, textAlign: 'center', fontSize: '11px' }}
                                    >
                                      {label}
                                    </span>
                                    {showDifferentiator && (
                                      <small className="text-muted mr-1" style={{ fontSize: '11px' }}>
                                        · {suffix}
                                      </small>
                                    )}
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary"
                                      style={{ fontSize: '12px', wordBreak: 'break-all', flex: 1 }}
                                    >
                                      {url}
                                    </a>
                                    <Button
                                      variant={copiedLink === copyKey ? 'success' : 'outline-secondary'}
                                      size="sm"
                                      style={{ flexShrink: 0, fontSize: '11px', padding: '1px 8px' }}
                                      onClick={() => {
                                        const copyLabel = suffix ? `${country} - ${suffix}` : country;
                                        copyToClipboard(`${copyLabel} | ${url}`, copyKey);
                                      }}
                                    >
                                      {copiedLink === copyKey ? 'Copied!' : 'Copy'}
                                    </Button>
                                  </div>
                                );
                              })
                            )}
                            </>)}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      {showModal && (
        <PartnerForm
          show={showModal}
          partnerId={modalPartnerId}
          partnerName={modalPartnerName}
          // Threaded so the pre-screening visibility list can strip the
          // `${projectName}_${shortName}_` prefix and render just the suffix.
          projectName={projectName || ''}
          surveyId={parentSurveyId!}
          surveyStatus={surveyType || 'Open'}
          modalType={modalType}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalSubmit}
          clientsQuota={userLimitCommitted}
          childCountries={childCountries}
          childPrescreenConfigs={childPrescreenConfigs}
          isParentSurvey={isParentSurvey}
          isUniqueLinks={survey?.useUniqueLinks}
          // A standalone survey lists only itself in childSurveys.
          hasChildMarkets={isParentSurvey && childSurveys.some((c) => c.id !== parentSurveyId)}
        />
      )}
    </div>
  );
}
